// Personalized recommendation engine.
//
// Given a user's completed mastery skill numbers, returns three
// concrete next-actions: the next skill to take on, an agent template
// worth exploring, and a tool from the directory that pairs with what
// they just learned. All logic is pure, sync, and runs client-side off
// the existing local data sources — no DB call needed, so it works for
// both signed-in and signed-out users.
import { AI_MASTERY_PATH, type MasterySkill, type MasteryTier } from "@/data/aiMasteryPath";
import { MASTERY_SURFACE_MAP, type SkillSurface } from "@/data/masterySurfaceMap";
import { allTools, type AITool } from "@/data/categories";

export interface SkillRec {
  kind: "skill";
  skill: MasterySkill;
  surface: SkillSurface | null;
  reason: string;
}

export interface AgentRec {
  kind: "agent";
  title: string;
  blurb: string;
  surface: string;
  cta: string;
  reason: string;
}

export interface ToolRec {
  kind: "tool";
  tool: AITool;
  reason: string;
}

export interface Recommendations {
  skill: SkillRec | null;
  agent: AgentRec | null;
  tool: ToolRec | null;
  /** Plain-language summary of what the user has done so far. */
  summary: string;
}

// Tier → which tool categories pair best with what the user just learned.
// Order matters — first match wins for the trending tool pick.
const TIER_TOOL_CATEGORIES: Record<MasteryTier, string[]> = {
  Awareness: ["AI Chatbots", "AI Writing", "AI Research"],
  Application: ["AI Writing", "AI Email", "AI Presentation", "AI Marketing"],
  Leverage: ["AI Workflow Automation", "AI Marketing", "AI Sales", "AI SEO"],
  Systems: ["AI Coding", "AI Workflow Automation", "AI Customer Service"],
  Architecture: ["AI Coding", "AI DevOps", "AI Security", "AI Productivity"],
};

// Tier → suggested agent template + the surface that opens it.
const TIER_AGENT: Record<MasteryTier, Omit<AgentRec, "kind" | "reason">> = {
  Awareness: {
    title: "Daily Briefing Agent",
    blurb: "Summarizes anything you paste into 3 bullets — a perfect first agent.",
    surface: "/agents/builder?template=beginner",
    cta: "Build it",
  },
  Application: {
    title: "Email Reply Drafter",
    blurb: "Reads an email and proposes 3 friendly reply drafts in your voice.",
    surface: "/agents/builder?template=beginner",
    cta: "Build it",
  },
  Leverage: {
    title: "Outbound Personalizer",
    blurb: "Takes a list of leads + your offer and writes tailored openers.",
    surface: "/agents/builder?template=beginner",
    cta: "Build it",
  },
  Systems: {
    title: "Research Agent with Tools",
    blurb: "Web search + fetch — give it a question, get a cited brief.",
    surface: "/agents/builder",
    cta: "Open builder",
  },
  Architecture: {
    title: "Multi-Agent Workflow",
    blurb: "Researcher → writer → reviewer chain with handoffs.",
    surface: "/agents/builder",
    cta: "Open builder",
  },
};

function pickTool(tier: MasteryTier, completed: number[]): AITool | null {
  const categories = TIER_TOOL_CATEGORIES[tier] ?? [];
  // Deterministic per-progress: rotate by how many skills are done so the
  // user sees a different tool as they progress, but the same tool on
  // re-renders for the same progress count.
  const offset = completed.length;
  for (const cat of categories) {
    const matches = allTools.filter((t) => t.category === cat && t.trending);
    if (matches.length === 0) continue;
    return matches[offset % matches.length];
  }
  // Fallback: any trending tool.
  const trending = allTools.filter((t) => t.trending);
  if (trending.length === 0) return null;
  return trending[offset % trending.length];
}

function nextSkill(completed: number[]): MasterySkill | null {
  const done = new Set(completed);
  // Find the lowest skill number not yet completed.
  for (const skill of AI_MASTERY_PATH) {
    if (!done.has(skill.num)) return skill;
  }
  return null;
}

function summarize(completed: number[]): string {
  const n = completed.length;
  if (n === 0) return "Brand new — let's get your first win.";
  if (n < 5) return `${n} skill${n === 1 ? "" : "s"} down. The first 5 unlock everything else.`;
  if (n < 20) return `${n} skills mastered — you're past the awkward phase.`;
  if (n < 40) return `${n} skills in. You can already out-produce most knowledge workers.`;
  if (n < 60) return `${n} skills — you're operating at Leverage tier.`;
  if (n < 80) return `${n} skills — Systems thinker territory.`;
  if (n < 100) return `${n}/100. Architect-level. The last 20 are the hardest.`;
  return "100/100. You finished. Now go build the future.";
}

export function buildRecommendations(completed: number[]): Recommendations {
  const next = nextSkill(completed);
  const tier: MasteryTier = next?.tier ?? "Architecture";

  const skillRec: SkillRec | null = next
    ? {
        kind: "skill",
        skill: next,
        surface: MASTERY_SURFACE_MAP[next.num] ?? null,
        reason:
          completed.length === 0
            ? "Start here — it's the foundation."
            : `Next in ${next.tier} — builds on what you just did.`,
      }
    : null;

  const agentBase = TIER_AGENT[tier];
  const agentRec: AgentRec = {
    kind: "agent",
    ...agentBase,
    reason: `Matches your level (${tier}). Most ${tier}-tier hackers ship this kind of agent first.`,
  };

  const tool = pickTool(tier, completed);
  const toolRec: ToolRec | null = tool
    ? {
        kind: "tool",
        tool,
        reason: `Trending in ${tool.category} — pairs well with your current skills.`,
      }
    : null;

  return {
    skill: skillRec,
    agent: agentRec,
    tool: toolRec,
    summary: summarize(completed),
  };
}